import { IOfferSummaryCardAdditionalPrice } from '@vfde-brix/ws10/offer-summary-card';
import { ProductContainerActions, ProductContainerProps } from '../../container/Product';
import { Device, Option, OverlayView, ProductType } from '../../enum';
import { CmsSummaryAdditionalPrice, CmsSummaryCardAdditionalText, CmsSummaryCardButton, CmsSummaryCardHeadline, CmsSummaryCardLegalText, CmsSummaryCardSubline } from '../../interface';
import { formatText } from '../formatter';
import { JarvisPriceAddons } from '@vfde-sails/availability';
import { Price } from '@vfde-brix/ws10/core';
import { getMeinVodafonePageUrl, getOptionPageUrl } from '../url';
import { createTelUrl } from '@vfde-sails/utils';

const getHeadlineText = (
    props: ProductContainerProps,
    headlines: CmsSummaryCardHeadline,
) => {
    if (props.isNonMonthlyBilling) {
        return headlines.nonMonthlyBilling;
    }

    if (props.isBookingDisabled) {
        return headlines.bookingDisabled;
    }

    if (props.hasTvContract) {
        return headlines.alreadyHasTv;
    }

    return props.selected.type === ProductType.CABLE
        ? headlines.cable
        : headlines.net;
};

const getSublineTexts = (
    props: ProductContainerProps,
    sublines: CmsSummaryCardSubline,
): string | string[] => {
    if (props.isNonMonthlyBilling) {
        return Object.values(sublines.nonMonthlyBilling);
    }

    if (props.isBookingDisabled) {
        return sublines.bookingDisabled;
    }

    if (props.hasTvContract) {
        return sublines.alreadyHasTv;
    }

    if (props.selected.device === Device.HOMEBOX) {
        switch (props.selected.option) {
            case Option.NETFLIX: return sublines.homeboxNetflix;
            case Option.PREMIUM: return sublines.homeboxPremium;
            default: return sublines.homebox;
        }
    }

    switch (props.selected.option) {
        case Option.NETFLIX: return sublines.soundboxNetflix;
        case Option.PREMIUM: return sublines.soundboxPremium;
        default: return sublines.soundbox;
    }
};

const getSublineText = (
    props: ProductContainerProps,
    sublines: CmsSummaryCardSubline,
): string[] => {
    const texts = getSublineTexts(props, sublines);

    return Array.isArray(texts) ? texts : [ texts ];
};

const getAdditionalTexts = (
    props: ProductContainerProps,
    texts: CmsSummaryCardAdditionalText,
): string[] | undefined => {
    if (props.isBookingDisabled) {
        return [ texts.bookingDisabled ];
    }

    if (props.isNonMonthlyBilling) {
        return [ texts.nonMonthlyBilling ];
    }

    if (props.hasTvContract) {
        return Object.values(texts.alreadyHasTv);
    }

    if (!props.isLoggedIn) {
        return [ formatText(texts.unlogged, getMeinVodafonePageUrl()) ];
    }

    return undefined;
};

const getAdditionalPrice = (price: number, text: string, replacement?: string | number): IOfferSummaryCardAdditionalPrice => ({
    price: Price.fromNumber(price),
    txtLabel: replacement ? formatText(text, replacement.toString()) : text,
});

const getGigakombiDiscount = (priceAddons: JarvisPriceAddons) =>
    priceAddons.gigakombiDiscountTV?.price.value ?? 0;

const getDiscountedValue = (price: number, discount: number) =>
    parseFloat((price + discount).toFixed(10));

const getAdditionalPrices = (
    {
        product,
        isGigakombi,
        isLoggedIn,
    }: ProductContainerProps,
    cmsProps: CmsSummaryAdditionalPrice
): IOfferSummaryCardAdditionalPrice[] => {
    const result: IOfferSummaryCardAdditionalPrice[] = [];

    if (!product) {
        return result;
    }

    const { price, priceAddons } = product;
    const { provisionFee, deliveryFee } = priceAddons;

    if (price.stairway) {
        const stairwayPrice = isGigakombi && isLoggedIn
            ? getDiscountedValue(price.value, getGigakombiDiscount(priceAddons))
            : price.value;

        result.push(getAdditionalPrice(stairwayPrice, cmsProps.stairway, price.stairway.period + 1));
    }

    if (provisionFee) {
        result.push(getAdditionalPrice(provisionFee.price.value, cmsProps.provisionFee));
    }

    if (deliveryFee) {
        result.push(getAdditionalPrice(deliveryFee.price.value, cmsProps.deliveryFee));
    }

    return result;
};

const getPrice = ({ product, isGigakombi, isLoggedIn }: ProductContainerProps) => {
    if (!product) {
        return undefined;
    }

    const price = product.price.stairway?.value ?? product.price.value;

    if (isGigakombi && isLoggedIn) {
        return Price.fromNumber(getDiscountedValue(price, getGigakombiDiscount(product.priceAddons)));
    }

    return Price.fromNumber(price);
};

const getLegalText = (
    props: ProductContainerProps,
    texts: CmsSummaryCardLegalText,
): string[] | undefined => {
    if (props.isGigakombi && props.isLoggedIn) {
        return Object.values(texts.gigakombi);
    }

    return Object.values(texts.regular);
};

const getButtonText = (
    props: ProductContainerProps,
    texts: CmsSummaryCardButton
) => {
    if (props.isNonMonthlyBilling) {
        return texts.nonMonthlyBilling;
    }

    if (props.isBookingDisabled) {
        return texts.bookingDisabled;
    }

    if (props.hasTvContract) {
        return texts.alreadyHasTv;
    }

    if (props.isGigakombi && !props.isLoggedIn) {
        return texts.loginAndCheckAvailability;
    }

    if (props.availabilityResult || props.selected.type === ProductType.NET) {
        return texts.continueToOptionPage;
    }

    return texts.availabilityCheck;
};

const getButtonAction = (
    props: ProductContainerProps,
    actions: ProductContainerActions
) => {
    if (
        props.hasTvContract ||
        props.isNonMonthlyBilling ||
        props.isBookingDisabled ||
        (!props.isLoggedIn && props.isGigakombi) ||
        props.availabilityResult ||
        props.selected.type === ProductType.NET
    ) {
        return undefined;
    }

    return () => actions.setOverlayVisibility(OverlayView.AVAILABILITY_FORM);
};

const getButtonLink = (
    props: ProductContainerProps,
    cmsProps: CmsSummaryCardButton,
) => {
    if (props.isNonMonthlyBilling) {
        return createTelUrl(cmsProps.nonMonthlyBilling);
    }

    if (props.isBookingDisabled) {
        return createTelUrl(cmsProps.bookingDisabled);
    }

    if (props.hasTvContract) {
        return createTelUrl(cmsProps.alreadyHasTv);
    }

    if (props.isGigakombi && !props.isLoggedIn) {
        return getMeinVodafonePageUrl();
    }

    if ((props.availabilityResult || props.selected.type === ProductType.NET) && props.selectedId) {
        return getOptionPageUrl(props.selectedId, props.tvConnectBv?.id);
    }

    return undefined;
};

export default {
    getHeadlineText,
    getSublineText,
    getPrice,
    getAdditionalPrices,
    getLegalText,
    getButtonText,
    getButtonAction,
    getButtonLink,
    getAdditionalTexts,
};
